import React, { useEffect, useState } from 'react';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { apiCallJson } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

const LOCAL_STORAGE_KEY = 'bidoryx_onboarding_card_dismissed';

/**
 * Compact welcome banner shown at the top of the dashboard. Steers new users
 * to the "Get started" tab. Hides itself when:
 *   - The user clicks the close button or "Show me how"
 *   - Onboarding is fully complete (server gates this in the status payload)
 *   - 14 days have passed since signup (server gates this too)
 *
 * Uses localStorage as a fast-path optimization to avoid flicker on subsequent
 * dashboard mounts before the API response comes back.
 */
export default function OnboardingStickyCard({ onNavigate }) {
  const { theme } = useTheme();
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(LOCAL_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (hidden) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiCallJson('/api/onboarding/status');
        if (!cancelled && data?.success) setStatus(data);
      } catch {
        // Silent fail — the card is non-critical UX, never block the dashboard.
      }
    })();
    return () => { cancelled = true; };
  }, [hidden]);

  const dismiss = async ({ navigateTo } = {}) => {
    try { localStorage.setItem(LOCAL_STORAGE_KEY, 'true'); } catch {}
    setHidden(true);
    // Fire-and-forget. Don't block navigation on the API.
    apiCallJson('/api/onboarding/dismiss-card', { method: 'POST' }).catch(() => {});
    if (navigateTo && typeof onNavigate === 'function') onNavigate(navigateTo);
  };

  if (hidden || !status) return null;
  if (!status.stickyCardVisible) return null;

  const { completedCount, total, progressPct } = status;

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: 18,
      padding: '18px 22px',
      borderRadius: 16,
      background: theme.colors.brand.gradient,
      color: '#ffffff',
      marginBottom: 24,
      boxShadow: '0 12px 28px rgba(99, 102, 241, 0.28)',
      position: 'relative',
      overflow: 'hidden'
    },
    iconWrap: {
      width: 44,
      height: 44,
      borderRadius: 12,
      flexShrink: 0,
      background: 'rgba(255, 255, 255, 0.18)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    body: {
      flex: 1,
      minWidth: 0
    },
    title: {
      fontSize: 16,
      fontWeight: 700,
      marginBottom: 4
    },
    sub: {
      fontSize: 13,
      color: 'rgba(255, 255, 255, 0.85)',
      lineHeight: 1.45
    },
    progressOuter: {
      width: '100%',
      height: 4,
      borderRadius: 999,
      background: 'rgba(255, 255, 255, 0.25)',
      overflow: 'hidden',
      marginTop: 10
    },
    progressInner: {
      height: '100%',
      width: `${progressPct}%`,
      background: '#ffffff',
      transition: 'width 0.4s ease'
    },
    cta: {
      flexShrink: 0,
      padding: '10px 16px',
      borderRadius: 10,
      border: 'none',
      background: '#ffffff',
      color: theme.colors.brand.primary,
      fontSize: 14,
      fontWeight: 700,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 6
    },
    close: {
      position: 'absolute',
      top: 10,
      right: 10,
      background: 'transparent',
      border: 'none',
      color: 'rgba(255, 255, 255, 0.85)',
      cursor: 'pointer',
      padding: 4,
      borderRadius: 6,
      lineHeight: 0
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.iconWrap}>
        <Sparkles size={22} color="#ffffff" />
      </div>
      <div style={styles.body}>
        <div style={styles.title}>
          Get the most out of Bidoryx ({completedCount}/{total})
        </div>
        <div style={styles.sub}>
          Finish a few quick setup steps so the AI can do its best work for your account.
        </div>
        <div style={styles.progressOuter}>
          <div style={styles.progressInner} />
        </div>
      </div>
      <button
        style={styles.cta}
        onClick={() => dismiss({ navigateTo: 'getting-started' })}
      >
        Show me how
        <ArrowRight size={16} />
      </button>
      <button
        style={styles.close}
        onClick={() => dismiss()}
        aria-label="Dismiss"
        title="Dismiss"
      >
        <X size={18} />
      </button>
    </div>
  );
}
